/**
 * Keeps track of index of the datum where
 * the D3 CurveGenerator is working on. It Also keeps track whether we are
 * starting or ending a section of the data set curve.
 */
export class IndexTracker {
    index = 0;
    _lineStartIndex;
    _lineEndIndex;
    constructor() {
        this._lineStartIndex = undefined;
        this._lineEndIndex = undefined;
    }
    /**
     * Gets called by the function given as an argument to the d3.Line.define() step.
     * @see getSeriesSections in time-series.ts for the function itself
     * @param index The index of the current datum
     */
    setIndex(index) {
        this.index = index;
    }
    getIndex() {
        return this.index;
    }
    /**
     * Called by the d3.CurveGenerator, telling us that a new section is starting.
     */
    markLineStart() {
        this._lineStartIndex = this.index;
        this._lineEndIndex = undefined;
    }
    /**
     * Called by the d3.CurveGenerator, telling us that a section has ended.
     */
    markLineEnd() {
        this._lineEndIndex = this.index;
    }
    //getter
    lineStart() {
        return this._lineStartIndex;
    }
    //getter
    lineEnd() {
        return this._lineEndIndex;
    }
    toString() {
        return `{ index: ${this.index}, lineStart: ${this._lineStartIndex}, lineEnd: ${this._lineEndIndex} }`;
    }
}
/**
 * This is a D3 context for generating lines/curves. Its is a partial
 * implementation of the CanvasRenderingContext2D interface.
 * This context 'draws' (generates actually) the segments that the
 * d3.CurveGenerator tells us to draw/generate. We focus on line segments
 * only so not every d3CurveGenerator will work here. All segments that
 * are handled here are converted to bezier curves.
 * //We also attach the parametric properties to the bezier curves. I.E.
 * //at which instant in time does each segment start and how much time
 * //does the curve section represents.
 *
 * So basically this context generates a spline
 */
export class SplineContext {
    _x;
    _y;
    //  private _t: number | undefined;
    _startCurveSectionIndex;
    //current curve section
    _curve;
    // the _indexTracker will tell us at what index the current curve section started
    _indexTracker;
    //An array of curves that make up the complete data set. Since the
    //data set can contain holes we have an array of curves instead of
    //one single curve. A curve consists of a string of BezierCurves
    //knitted together (hence we need an array for that)
    _;
    constructor(
    //    private time: Timing
    ) {
        this._indexTracker = new IndexTracker();
        this._ = [];
    }
    indexTracker() {
        return this._indexTracker;
    }
    //move to a new section of the data set curve
    moveTo(x, y) {
        //console.log(`'moveTo called ${this._indexTracker}`);
        this._x = +x;
        this._y = +y;
        this._startCurveSectionIndex = this._indexTracker.lineStart() ?? 0;
        //get the epoch for the start of this curve
        //    this._t = this.time.get(this._startCurveSectionIndex);
        //start a new curve section
        if (this._.length === 0 || this._[this._.length - 1].length > 0) {
            this._curve = [];
            this._.push(this._curve);
        }
    }
    closePath() {
        //console.log(`'closePath called ${this._indexTracker}`);
    }
    lineTo(x, y) {
        //console.log(`'lineTo called ${this._indexTracker}`);
        this.bezierCurveTo(x, y, this._x, this._y, x, y);
    }
    quadraticCurveTo(x1, y1, x, y) {
        //console.log(`'quadraticCurveTo called ${this._indexTracker}`);
        const xcp1 = this._x + (2 * (x1 - this._x)) / 3;
        const ycp1 = this._y + (2 * (y1 - this._y)) / 3;
        const xcp2 = x + (2 * (x1 - x)) / 3;
        const ycp2 = y + (2 * (y1 - y)) / 3;
        this.bezierCurveTo(xcp1, ycp1, xcp2, ycp2, x, y);
    }
    bezierCurveTo(x1, y1, x2, y2, x, y) {
        //console.log(`'bezierCurveTo called ${this._indexTracker}`);
        if (this._curve) {
            const cp = [
                { x: this._x, y: this._y },
                { x: x1, y: y1 },
                { x: x2, y: y2 },
                { x, y },
            ];
            //get the epoch end time for this curve section
            //      const _t1 = this.time.get(<number>this._startCurveSectionIndex+this._curve.length+1);
            //      this._curve?.push({ cp, dT: _t1 - <number>this._t, t0: <number>this._t });
            this._curve?.push({ cp, length: 1.0 });
            this._x = x;
            this._y = y;
            //      this._t = _t1;
        }
        else {
            throw new Error('Call to bezierCurveTo without a call to moveTo first in. Starting coordinate is undefined.');
        }
    }
    arcTo(x1, y1, x2, y2, r) {
        //console.log(`'arcTo called ${this._timeKeeper.getIndex()}`);
    }
    arc(x, y, r, a0, a1, ccw) {
        //console.log(`'arc called ${this._timeKeeper.getIndex()}`);
    }
    rect(x, y, w, h) {
        //console.log(`'rect called ${this._timeKeeper.getIndex()}`);
    }
    getCurves() {
        //console.log('getCurves', this._);
        return this.popEmptyCurveSection();
    }
    popEmptyCurveSection() {
        if (this._.length > 0 && this._[this._.length - 1].length === 0) {
            //console.log('popping last');
            this._.pop();
        }
        return this._;
    }
}
export function splineContext(
//  time: Timing
) {
    //  return new SplineContext(time);
    return new SplineContext();
}
// Allow instanceof multiCurvePath
splineContext.prototype = SplineContext.prototype;
